/**
 * TemplateBuilder — Sprint 4.6
 *
 * Modal para criar/editar Config Template. Cada aba (Rede / Gerência /
 * Watchdog / Agenda / Avançado) tem checkbox "Incluir no template". Só
 * abas marcadas vão para payload_json. Se múltiplas marcadas → scope=mixed.
 *
 * Não é o DeviceConfigModal (que aplica em 1 device) — aqui apenas MONTAMOS
 * o payload reusando os mesmos schemas. Apply em massa é em TemplateApplyModal.
 */
import { useEffect, useState } from "react";
import {
  createConfigTemplate,
  updateConfigTemplate,
} from "../../lib/api.js";
import { t } from "../../i18n/index.js";
import ScheduleEditor from "../schedule/ScheduleEditor.jsx";

// Sprint UX-BR-3 — labels traduzíveis. `id`/`cfg` permanecem técnicos
// (são chaves do backend); apenas `label` passa por t().
const ALL_KEYS = [
  { id: "wifi",       cfg: "config_wifi",       labelKey: "tabs.wifi",       icon: "📶" },
  { id: "management", cfg: "config_management", labelKey: "tabs.management", icon: "⚙" },
  { id: "watchdog",   cfg: "config_watchdog",   labelKey: "tabs.watchdog",   icon: "🐕" },
  { id: "schedule",   cfg: "config_schedule",   labelKey: "tabs.schedule",   icon: "🗓" },
  { id: "advanced",   cfg: "config_advanced",   labelKey: "tabs.advanced",   icon: "🛠" },
];

// HOTFIX s46.1 — defaults populados no PRIMEIRO render (não no useEffect).
// Antes: useState({}) crashava porque os Forms tentavam ler data.<key>.enabled
// antes do useEffect rodar.
function buildInitialData() {
  const d = {};
  ALL_KEYS.forEach(k => { d[k.id] = defaultData(k.id); });
  return d;
}

function buildInitialIncluded() {
  const i = {};
  ALL_KEYS.forEach(k => { i[k.id] = false; });
  return i;
}

export default function TemplateBuilder({ open, template, onClose, onSaved }) {
  const isEdit = !!template?.id;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [activeTab, setActiveTab] = useState("watchdog");
  // checkbox "incluir" por chave
  // HOTFIX s46.1 — populado já no primeiro render (evita crash no Form)
  const [included, setIncluded] = useState(buildInitialIncluded);
  // dados de cada chave (sempre populados c/ defaults — só envia se included)
  const [data, setData] = useState(buildInitialData);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Init ao abrir
  useEffect(() => {
    if (!open) return;
    if (template) {
      setName(template.name || "");
      setDescription(template.description || "");
      setEnabled(template.enabled !== false);
      const inc = {};
      const dat = {};
      const pj = template.payload_json || {};
      ALL_KEYS.forEach(k => {
        if (k.id === "wifi") {
          // Sprint 4.6.3 — aba "Rede" cobre config_wifi + config_network.
          // Incluída se qualquer um dos dois estiver no template.
          const hasWifi    = !!pj.config_wifi;
          const hasNetwork = !!pj.config_network;
          inc[k.id] = hasWifi || hasNetwork;
          const merged = { ...defaultData("wifi") };
          if (pj.config_wifi) {
            if (pj.config_wifi.ssid     !== undefined) merged.ssid     = pj.config_wifi.ssid;
            if (pj.config_wifi.password !== undefined) merged.password = pj.config_wifi.password;
            if (pj.config_wifi.hidden   !== undefined) merged.hidden   = pj.config_wifi.hidden;
          }
          if (pj.config_network) {
            const n = pj.config_network;
            merged.dhcp = n.dhcp !== false;          // padrão true se não vier
            if (n.ip !== undefined && n.ip !== null)           merged.ip = n.ip;
            if (n.mask !== undefined && n.mask !== null)       merged.mask = n.mask;
            if (n.gateway !== undefined && n.gateway !== null) merged.gateway = n.gateway;
            if (n.dns1 !== undefined && n.dns1 !== null)       merged.dns1 = n.dns1;
            if (n.dns2 !== undefined && n.dns2 !== null)       merged.dns2 = n.dns2;
          }
          dat[k.id] = merged;
        } else if (k.id === "management") {
          // Sprint 4.6.4 — mapeia chaves legadas (ntp/led) para nomes oficiais
          // (ntp_server/led_status) ao carregar template antigo.
          inc[k.id] = !!pj[k.cfg];
          if (pj[k.cfg]) {
            const m = pj[k.cfg];
            dat[k.id] = {
              hostname: m.hostname || "",
              timezone: m.timezone || "America/Bahia",
              ntp_server: m.ntp_server || m.ntp || "",
              ...(typeof m.led_status === "boolean" ? { led_status: m.led_status }
                : typeof m.led === "boolean" ? { led_status: m.led }
                : {}),
              ...(m.admin_password ? { admin_password: m.admin_password } : {}),
            };
          } else {
            dat[k.id] = defaultData("management");
          }
        } else {
          inc[k.id] = !!pj[k.cfg];
          dat[k.id] = pj[k.cfg] || defaultData(k.id);
        }
      });
      setIncluded(inc);
      setData(dat);
      // primeira aba incluída ou watchdog
      const firstInc = ALL_KEYS.find(k => inc[k.id]);
      setActiveTab(firstInc?.id || "watchdog");
    } else {
      // novo
      setName("");
      setDescription("");
      setEnabled(true);
      const inc = {};
      const dat = {};
      ALL_KEYS.forEach(k => {
        inc[k.id] = false;
        dat[k.id] = defaultData(k.id);
      });
      setIncluded(inc);
      setData(dat);
      setActiveTab("watchdog");
    }
    setErr(null);
  }, [open, template]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const includedKeys = ALL_KEYS.filter(k => included[k.id]);
  // Sprint 4.6.3 — aba "wifi" pode gerar 1 (config_wifi) ou 2 (+config_network) chaves
  const wifiAlsoEmitsNetwork = included.wifi && networkTouched(data.wifi);
  const effectiveKeyCount = includedKeys.length + (wifiAlsoEmitsNetwork ? 1 : 0);
  const computedScope = effectiveKeyCount === 0
    ? null
    : effectiveKeyCount === 1
      ? (includedKeys[0].id === "wifi" && wifiAlsoEmitsNetwork
          ? "network"  // edge case: aba wifi habilitada mas só network será emitido (improvável; SSID é obrigatório)
          : includedKeys[0].id)
      : "mixed";

  const updateData = (key, partial) => {
    setData(prev => ({ ...prev, [key]: { ...prev[key], ...partial } }));
  };

  const toggleInclude = (key) => {
    setIncluded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const submit = async () => {
    if (!name.trim()) { setErr(t("templateBuilder.err.nameRequired")); return; }
    if (includedKeys.length === 0) {
      setErr(t("templateBuilder.err.includeAtLeastOne"));
      return;
    }

    // Sprint 4.6.3 — Validação client-side da aba Rede (config_network)
    if (included.wifi) {
      const w = data.wifi || {};
      // SSID obrigatório (mesmo critério do backend ConfigWifiIn)
      if (!w.ssid || !w.ssid.trim()) {
        setErr(t("templateBuilder.err.networkSsidRequired"));
        return;
      }
      // Network (DHCP/IP estático) — pode não ser enviado se intocado
      const netErr = validateNetwork(w);
      if (netErr) { setErr(netErr); return; }
    }

    // Sprint 4.6.1 — Validação client-side antes de enviar
    if (included.schedule) {
      const sched = data.schedule || {};
      if (sched.enabled) {
        const entries = (Array.isArray(sched.entries) ? sched.entries : [])
          .filter((e) => {
            if (!e || !Array.isArray(e.days) || e.days.length === 0) return false;
            // Modo pulse: time + duration_s 1-300
            if (e.time !== undefined && e.time !== null && e.time !== "") {
              return e.duration_s >= 1 && e.duration_s <= 300;
            }
            // Modo janela: on_time + off_time diferentes
            return e.on_time && e.off_time && e.on_time !== e.off_time;
          });
        if (entries.length === 0) {
          setErr(t("templateBuilder.err.scheduleNoEntries"));
          return;
        }
      }
    }

    // Sprint 4.6.2 — Validação client-side Advanced (mesmo ranges do backend ConfigAdvancedIn)
    if (included.advanced) {
      const a = data.advanced || {};
      const errs = [];
      const hb = Number(a.heartbeat_interval);
      if (!Number.isFinite(hb) || hb < 30 || hb > 600) {
        errs.push(`Heartbeat (s) deve estar entre 30 e 600 (recebido ${a.heartbeat_interval})`);
      }
      const rssi = Number(a.rssi_threshold);
      if (!Number.isFinite(rssi) || rssi < -95 || rssi > -30) {
        errs.push(`RSSI threshold deve estar entre -95 e -30 dBm (recebido ${a.rssi_threshold})`);
      }
      if (!["restore", "on", "off"].includes(a.relay_boot)) {
        errs.push(`Relé default boot deve ser 'restore', 'on' ou 'off' (recebido ${a.relay_boot})`);
      }
      const ar = Number(a.auto_reboot);
      if (!Number.isFinite(ar) || ar < 0 || ar > 365) {
        errs.push(`Auto reboot deve estar entre 0 e 365 dias (recebido ${a.auto_reboot})`);
      }
      if (errs.length > 0) {
        setErr(t("templateBuilder.err.advancedPrefix") + errs.join("\n"));
        return;
      }
    }

    setBusy(true); setErr(null);
    try {
      const payload_json = {};
      includedKeys.forEach(k => {
        if (k.id === "wifi") {
          // Sprint 4.6.3 — Aba "Rede" gera DOIS payloads:
          // 1. config_wifi: SEMPRE (SSID/senha/hidden)
          payload_json["config_wifi"] = sanitizePayload("wifi", data.wifi);
          // 2. config_network: só se usuário tocou em DHCP/IP
          const net = sanitizeNetwork(data.wifi);
          if (net !== null) payload_json["config_network"] = net;
        } else {
          payload_json[k.cfg] = sanitizePayload(k.id, data[k.id]);
        }
      });
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        scope: computedScope,
        payload_json,
        enabled,
      };
      let saved;
      if (isEdit) {
        saved = await updateConfigTemplate(template.id, body);
      } else {
        saved = await createConfigTemplate(body);
      }
      onSaved?.(saved);
      onClose();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[2100] bg-black/70 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-noc-panel border border-noc-accent/30 rounded shadow-2xl w-full max-w-3xl flex flex-col"
           style={{ maxHeight: "92vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-noc-border">
          <h3 className="text-sm font-semibold text-noc-accent">
            {isEdit ? t("templateBuilder.titleEdit") : t("templateBuilder.titleNew")}
            {computedScope && (
              <span className="ml-3 text-[10px] font-mono px-2 py-0.5 rounded border border-noc-accent/30 bg-noc-accent/10 text-noc-accent">
                scope={computedScope}
              </span>
            )}
          </h3>
          <button type="button" onClick={onClose}
            className="text-noc-muted hover:text-noc-text text-xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Meta */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="block text-xs space-y-1 sm:col-span-2">
              <span className="text-noc-muted">{t("templateBuilder.name")}</span>
              <input value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t("templateBuilder.namePlaceholder")}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
            </label>
            <label className="flex items-center gap-2 text-xs mt-5 sm:mt-6">
              <input type="checkbox" checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)} />
              <span>{t("templateBuilder.enabled")}</span>
            </label>
          </div>
          <label className="block text-xs space-y-1">
            <span className="text-noc-muted">{t("templateBuilder.descriptionLabel")}</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} maxLength={2000}
              placeholder={t("templateBuilder.descriptionPlaceholder")}
              className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 text-noc-text" />
          </label>

          {/* Tabs */}
          <div className="flex border-b border-noc-border mt-2">
            {ALL_KEYS.map(k => (
              <button key={k.id} type="button"
                onClick={() => setActiveTab(k.id)}
                className={`px-3 py-1.5 text-[11px] border-b-2 ${
                  activeTab === k.id
                    ? "border-noc-accent/30 text-noc-accent"
                    : "border-transparent text-noc-muted hover:text-noc-text"
                }`}>
                <span className="mr-1">{k.icon}</span>
                {t(k.labelKey)}
                {included[k.id] && <span className="ml-1 text-noc-ok">●</span>}
              </button>
            ))}
          </div>

          {/* Include checkbox + form */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs font-semibold text-noc-accent">
              <input type="checkbox" checked={!!included[activeTab]}
                onChange={() => toggleInclude(activeTab)} />
              {t("templateBuilder.includeInTemplate", { section: activeTab })}
            </label>
            <div className={`${included[activeTab] ? "" : "opacity-40 pointer-events-none"}`}>
              {activeTab === "wifi"       && <WifiForm       data={data.wifi}       onChange={(p) => updateData("wifi", p)} />}
              {activeTab === "management" && <ManagementForm data={data.management} onChange={(p) => updateData("management", p)} />}
              {activeTab === "watchdog"   && <WatchdogForm   data={data.watchdog}   onChange={(p) => updateData("watchdog", p)} />}
              {activeTab === "schedule"   && <ScheduleForm   data={data.schedule}   onChange={(p) => updateData("schedule", p)} />}
              {activeTab === "advanced"   && <AdvancedForm   data={data.advanced}   onChange={(p) => updateData("advanced", p)} />}
            </div>
          </div>

          {/* Sumário do payload */}
          {includedKeys.length > 0 && (
            <div className="mt-3 bg-noc-bg/40 border border-noc-accent/30 rounded p-2 text-[10px] text-noc-muted">
              <div className="text-noc-accent mb-1 uppercase tracking-wide">{t("templateBuilder.payloadFinal")}</div>
              <div className="font-mono">
                {includedKeys.map(k => k.cfg).join(" + ")}
              </div>
            </div>
          )}

          {err && (
            <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-noc-border flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text">
            {t("templateBuilder.cancel")}
          </button>
          <button type="button" onClick={submit} disabled={busy}
            className="px-3 py-1.5 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold disabled:opacity-50">
            {busy ? t("templateBuilder.saving") : (isEdit ? t("templateBuilder.save") : t("templateBuilder.create"))}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Defaults + sanitize por chave
// ============================================================================

function defaultData(key) {
  switch (key) {
    case "wifi":
      // Sprint 4.6.3 — defaults: DHCP marcado + campos IP vazios.
      // Assim, networkTouched()=false → não envia config_network por padrão.
      // Quando usuário desmarcar DHCP, WifiForm sugere dns1=8.8.8.8/dns2=1.1.1.1.
      return { ssid: "", password: "", hidden: false, dhcp: true,
               ip: "", mask: "", gateway: "", dns1: "", dns2: "" };
    case "management":
      // Sprint 4.6.4 — ConfigManagementIn aceita ntp_server (não ntp)
      return { hostname: "", timezone: "America/Bahia", ntp_server: "pool.ntp.org" };
    case "watchdog":
      return { enabled: true, host1: "8.8.8.8", host2: "1.1.1.1",
               mode: "ALL_FAIL", action: "relay_reboot",
               interval_s: 60, timeout_s: 5, reboot_after_fails: 3, cooldown_s: 300 };
    case "schedule":
      return { enabled: false, entries: [], exceptions: [] };
    case "advanced":
      return { heartbeat_interval: 60, rssi_threshold: -75, relay_boot: "restore",
               ota_auto: false, safe_mode: false, auto_reboot: 0 };
    default:
      return {};
  }
}

function sanitizePayload(key, d) {
  // Remove campos vazios opcionais p/ não falhar Pydantic
  switch (key) {
    case "wifi": {
      // Sprint 4.6.3 — config_wifi PURO (sem dhcp/ip/mask/...).
      // Esses campos vão para config_network via sanitizeNetwork() abaixo.
      const out = { ssid: (d.ssid || "").trim(), password: d.password || "" };
      if (d.hidden) out.hidden = true;
      return out;
    }
    case "management": {
      // Sprint 4.6.4 — backend ConfigManagementIn aceita SÓ:
      //   admin_password, hostname, timezone, ntp_server, led_status
      // Mapeia o estado local 'ntp' (legado) para 'ntp_server' se ainda
      // existir; led_status só vem do DeviceConfigModal individual.
      const out = {};
      if (d.hostname) out.hostname = d.hostname.trim();
      if (d.timezone) out.timezone = d.timezone.trim();
      // Aceita state 'ntp_server' (novo) ou 'ntp' (legado, fallback)
      const ntp = d.ntp_server || d.ntp;
      if (ntp) out.ntp_server = ntp.trim();
      // led_status: só envia se foi setado (true/false), nunca null
      if (typeof d.led_status === "boolean") out.led_status = d.led_status;
      // admin_password só se o TemplateBuilder vier a expor o campo um dia
      if (d.admin_password) out.admin_password = d.admin_password;
      return out;
    }
    case "watchdog": {
      const out = { enabled: !!d.enabled };
      if (d.enabled) {
        if (d.host1)    out.host1 = d.host1.trim();
        if (d.host2 && d.host2.trim()) out.host2 = d.host2.trim();
        if (d.mode)     out.mode = d.mode;
        if (d.action)   out.action = d.action;
        if (d.interval_s)         out.interval_s = Number(d.interval_s);
        if (d.timeout_s)          out.timeout_s = Number(d.timeout_s);
        if (d.reboot_after_fails) out.reboot_after_fails = Number(d.reboot_after_fails);
        if (d.cooldown_s)         out.cooldown_s = Number(d.cooldown_s);
      }
      return out;
    }
    case "schedule": {
      // Sprint 4.6.1c — sanitiza entries para 2 modos (pulse + janela)
      const cleanEntries = (Array.isArray(d.entries) ? d.entries : [])
        .map((e) => {
          if (!e || !Array.isArray(e.days) || e.days.length === 0) return null;
          const isPulseMode = e.time !== undefined && e.time !== null && e.time !== "";
          if (isPulseMode) {
            // MODO PULSE
            if (!e.duration_s || e.duration_s < 1 || e.duration_s > 300) return null;
            return {
              days: e.days.slice().sort((a, b) => a - b),
              time: e.time,
              duration_s: Number(e.duration_s),
              action: e.action === "esp_reboot" ? "esp_reboot" : "relay_reboot",
              enabled: e.enabled !== false,
            };
          } else {
            // MODO JANELA
            if (!e.on_time || !e.off_time || e.on_time === e.off_time) return null;
            return {
              days: e.days.slice().sort((a, b) => a - b),
              on_time: e.on_time,
              off_time: e.off_time,
              action: e.action === "relay_off" ? "relay_off" : "relay_on",
              enabled: e.enabled !== false,
            };
          }
        })
        .filter(Boolean);
      const cleanExceptions = (Array.isArray(d.exceptions) ? d.exceptions : [])
        .filter((s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s));
      return {
        enabled: !!d.enabled,
        entries: cleanEntries,
        exceptions: cleanExceptions,
      };
    }
    case "advanced":
      return {
        heartbeat_interval: Number(d.heartbeat_interval),
        rssi_threshold:     Number(d.rssi_threshold),
        relay_boot:         d.relay_boot,
        ota_auto:           !!d.ota_auto,
        safe_mode:          !!d.safe_mode,
        auto_reboot:        Number(d.auto_reboot),
      };
    default:
      return d;
  }
}

// ============================================================================
// Forms (versão simplificada — mesma estrutura do DeviceConfigModal mas sem
// snapshot/DiffHint pois aqui estamos montando, não comparando)
// ============================================================================

function WifiForm({ data, onChange }) {
  data = data || defaultData("wifi");                            // HOTFIX s46.1

  // Sprint 4.6.3 — checa se há ALGUM campo IP preenchido (mesmo com DHCP marcado)
  // p/ mostrar hint discreto sobre o estado do envio
  const ipFieldsSet = ["ip","gateway","dns1","dns2"].some(
    (f) => typeof data[f] === "string" && data[f].trim().length > 0
  );

  const clearIpFields = () => {
    onChange({ ip: "", gateway: "", dns1: "", dns2: "", mask: "255.255.255.0" });
  };

  return (
    <div className="space-y-3">
      {/* --- WiFi (SSID + senha) --- */}
      <div className="text-[10px] text-noc-muted uppercase tracking-wide">Wi-Fi</div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="space-y-0.5">
          <span className="text-noc-muted">SSID</span>
          <input value={data.ssid || ""} onChange={(e) => onChange({ ssid: e.target.value })}
            placeholder="Nome da rede"
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
        </label>
        <label className="space-y-0.5">
          <span className="text-noc-muted">Senha</span>
          <input type="password" value={data.password || ""}
            onChange={(e) => onChange({ password: e.target.value })}
            placeholder="(em branco se aberta)"
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
        </label>
      </div>
      <label className="flex items-center gap-2 text-[11px]">
        <input type="checkbox" checked={!!data.hidden}
          onChange={(e) => onChange({ hidden: e.target.checked })} />
        <span>SSID oculto</span>
      </label>

      {/* --- Network (DHCP ou IP fixo) --- */}
      <div className="pt-2 border-t border-noc-border/50">
        <div className="text-[10px] text-noc-muted uppercase tracking-wide mb-1">
          IP — config_network <span className="text-noc-muted/50 normal-case">(opcional)</span>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={data.dhcp !== false}
            onChange={(e) => {
              const wantDhcp = e.target.checked;
              if (wantDhcp) {
                clearIpFields();
                onChange({ dhcp: true });
              } else {
                // Sprint 4.6.3 — ao desmarcar DHCP, sugere defaults úteis
                // (apenas para campos ainda vazios)
                const suggest = { dhcp: false };
                if (!data.mask) suggest.mask = "255.255.255.0";
                if (!data.dns1) suggest.dns1 = "8.8.8.8";
                if (!data.dns2) suggest.dns2 = "1.1.1.1";
                onChange(suggest);
              }
            }} />
          <span>DHCP automático</span>
        </label>

        {data.dhcp === false ? (
          <>
            <div className="grid grid-cols-2 gap-2 text-xs mt-2">
              {[
                { k: "ip", l: "IP", ph: "192.168.1.10" },
                { k: "mask", l: "Máscara", ph: "255.255.255.0" },
                { k: "gateway", l: "Gateway", ph: "192.168.1.1" },
                { k: "dns1", l: "DNS primário", ph: "8.8.8.8" },
                { k: "dns2", l: "DNS secundário (opcional)", ph: "1.1.1.1" },
              ].map(({ k, l, ph }) => (
                <label key={k} className="space-y-0.5">
                  <span className="text-noc-muted">{l}</span>
                  <input value={data[k] || ""}
                    onChange={(e) => onChange({ [k]: e.target.value })}
                    placeholder={ph}
                    className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
                </label>
              ))}
            </div>
            <div className="text-[10px] text-noc-warn mt-2">
              ⚠ IP fixo exige IP, máscara, gateway e DNS primário preenchidos.
              Se algo estiver fora da subnet alcançável, o device pode ficar inacessível.
            </div>
          </>
        ) : (
          <div className="text-[10px] text-noc-muted/60 mt-1">
            {ipFieldsSet
              ? "⚠ Há campos IP preenchidos mas DHCP está marcado. Eles serão IGNORADOS — desmarque DHCP se quiser usá-los."
              : "Não enviará config_network. Só envia se desmarcar DHCP ou preencher algum campo IP."}
          </div>
        )}
      </div>
    </div>
  );
}

function ManagementForm({ data, onChange }) {
  data = data || defaultData("management");                      // HOTFIX s46.1
  return (
    <div className="space-y-2 text-xs">
      {[
        { k: "hostname",   l: "Hostname",   ph: "sonoff-recepcao" },
        { k: "timezone",   l: "Timezone",   ph: "America/Bahia" },
        // Sprint 4.6.4 — state key ntp_server (não 'ntp')
        { k: "ntp_server", l: "NTP server", ph: "pool.ntp.org" },
      ].map(({ k, l, ph }) => (
        <label key={k} className="block space-y-0.5">
          <span className="text-noc-muted">{l}</span>
          <input value={data[k] || ""} onChange={(e) => onChange({ [k]: e.target.value })}
            placeholder={ph}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
        </label>
      ))}
    </div>
  );
}

function WatchdogForm({ data, onChange }) {
  data = data || defaultData("watchdog");                        // HOTFIX s46.1
  return (
    <div className="space-y-2 text-xs">
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={!!data.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })} />
        <span>Habilitar watchdog</span>
      </label>
      {data.enabled && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <label className="space-y-0.5">
              <span className="text-noc-muted">Host primário</span>
              <input value={data.host1 || ""} onChange={(e) => onChange({ host1: e.target.value })}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
            </label>
            <label className="space-y-0.5">
              <span className="text-noc-muted">Host secundário</span>
              <input value={data.host2 || ""} onChange={(e) => onChange({ host2: e.target.value })}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="space-y-0.5">
              <span className="text-noc-muted">Modo</span>
              <select value={data.mode || "ALL_FAIL"} onChange={(e) => onChange({ mode: e.target.value })}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text">
                <option value="ALL_FAIL">ALL_FAIL</option>
                <option value="ANY_FAIL">ANY_FAIL</option>
              </select>
            </label>
            <label className="space-y-0.5">
              <span className="text-noc-muted">Ação</span>
              <select value={data.action || "relay_reboot"} onChange={(e) => onChange({ action: e.target.value })}
                className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text">
                <option value="relay_reboot">relay_reboot</option>
                <option value="esp_reboot">esp_reboot</option>
                <option value="alert_only">alert_only</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[
              { k: "interval_s", l: "Intervalo (s)" },
              { k: "timeout_s", l: "Timeout (s)" },
              { k: "reboot_after_fails", l: "Falhas" },
              { k: "cooldown_s", l: "Cooldown (s)" },
            ].map(({ k, l }) => (
              <label key={k} className="space-y-0.5">
                <span className="text-noc-muted">{l}</span>
                <input type="number" value={data[k] ?? ""}
                  onChange={(e) => onChange({ [k]: e.target.value })}
                  className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Sprint 4.7.1 — Editor visual de agenda EXTRAÍDO para módulo
// compartilhado (ScheduleEditor) usado tanto pelo TemplateBuilder
// quanto pelo DeviceConfigModal/ScheduleTab.
//
// O wrapper abaixo mantém a API antiga (data, onChange) que o resto
// do TemplateBuilder espera. Import já feito no topo do arquivo.

function ScheduleForm({ data, onChange }) {
  data = data || defaultData("schedule");
  return <ScheduleEditor data={data} onChange={onChange} showExceptions />;
}


function AdvancedForm({ data, onChange }) {
  data = data || defaultData("advanced");                        // HOTFIX s46.1
  return (
    <div className="space-y-2 text-xs">
      <div className="grid grid-cols-2 gap-2">
        <label className="space-y-0.5">
          <span className="text-noc-muted">Heartbeat (s)</span>
          <input type="number" value={data.heartbeat_interval ?? 60} min={30} max={600}
            onChange={(e) => onChange({ heartbeat_interval: e.target.value })}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
        </label>
        <label className="space-y-0.5">
          <span className="text-noc-muted">RSSI threshold ({data.rssi_threshold ?? -75} dBm)</span>
          <input type="range" value={data.rssi_threshold ?? -75} min={-95} max={-30}
            onChange={(e) => onChange({ rssi_threshold: Number(e.target.value) })}
            className="w-full" />
        </label>
        <label className="space-y-0.5">
          <span className="text-noc-muted">Relé default boot</span>
          <select value={data.relay_boot || "restore"} onChange={(e) => onChange({ relay_boot: e.target.value })}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text">
            <option value="restore">restore</option>
            <option value="on">on</option>
            <option value="off">off</option>
          </select>
        </label>
        <label className="space-y-0.5">
          <span className="text-noc-muted">Auto reboot (dias, 0=off)</span>
          <input type="number" value={data.auto_reboot ?? 0} min={0} max={365}
            onChange={(e) => onChange({ auto_reboot: e.target.value })}
            className="w-full bg-noc-bg border border-noc-border rounded px-2 py-1 font-mono text-noc-text" />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!data.ota_auto}
            onChange={(e) => onChange({ ota_auto: e.target.checked })} />
          <span>OTA auto</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!data.safe_mode}
            onChange={(e) => onChange({ safe_mode: e.target.checked })} />
          <span>Safe mode</span>
        </label>
      </div>
    </div>
  );
}

// Sprint 4.6.1 / 4.6.2 — formatError robusto.
// O lib/api.js já formata e.message bem na maioria dos casos (com parse de
// pydantic repr e errors detalhados). Aqui complementamos para casos
// edge onde o caller olhar e.data.detail diretamente.
function formatError(e) {
  if (!e) return "Erro desconhecido";
  if (typeof e === "string") return e;

  // HTTP errors do lib/api.js carregam .status + .data com detail estruturado do FastAPI
  const data = e.data || e.body;
  if (data && typeof data === "object") {
    const detail = data.detail;
    if (typeof detail === "string") return detail;
    if (detail && typeof detail === "object") {
      // FastAPI 422: detail é array de { loc, msg, type }
      if (Array.isArray(detail)) {
        const parts = detail.map((d) => {
          const loc = Array.isArray(d.loc)
            ? d.loc.filter(x => x !== "body" && (typeof x === "string" || typeof x === "number")).join(".")
            : "";
          const msg = d.msg || d.message || JSON.stringify(d);
          return loc ? `${loc}: ${msg}` : msg;
        });
        return parts.join("\n");
      }
      // detail customizado de templates: { error: 'invalid_payload', errors: { config_X: [repr, ...] } }
      // PRIORIZA errors (detalhe) ANTES de error (genérico)
      if (detail.errors && typeof detail.errors === "object") {
        const lines = [];
        for (const [k, v] of Object.entries(detail.errors)) {
          if (Array.isArray(v)) {
            for (const item of v) {
              const p = _parsePyErr(item);
              lines.push(p.field ? `${k}.${p.field}: ${p.msg}` : `${k}: ${p.msg}`);
            }
          } else if (typeof v === "string") {
            lines.push(`${k}: ${v}`);
          }
        }
        if (lines.length > 0) return lines.join("\n");
      }
      if (detail.message) return String(detail.message);
      if (detail.error)   return String(detail.error);
      return JSON.stringify(detail);
    }
  }
  if (e.message) return String(e.message);
  try { return JSON.stringify(e); } catch { return String(e); }
}

// Mesma rotina do api.js — duplicada aqui para o formatError independente.
function _parsePyErr(s) {
  if (typeof s !== "string") return { field: "", msg: String(s) };
  const msgMatch = s.match(/'msg':\s*(["'])((?:\\.|(?!\1).)*)\1/);
  const locMatch = s.match(/'loc':\s*[\(\[]([^\)\]]+)[\)\]]/);
  let field = "";
  if (locMatch) {
    field = locMatch[1].split(",").map(p => p.trim().replace(/^['"]|['"]$/g, "")).filter(p => p && p !== "body").join(".");
  }
  const msg = msgMatch ? msgMatch[2] : s;
  return { field, msg };
}

// Sprint 4.6.3 — Helpers de config_network (separado de config_wifi).
// O backend tem 2 schemas DISTINTOS:
//   ConfigWifiIn    = { ssid, password, hidden }                                (extra="forbid")
//   ConfigNetworkIn = { dhcp, ip?, mask?, gateway?, dns1?, dns2? }              (extra="forbid")
// A aba "wifi" do TemplateBuilder gerencia ambos os payloads.

const _IPV4_RE = /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;

function _ipv4Ok(s) { return typeof s === "string" && _IPV4_RE.test(s.trim()); }

// Detecta se o usuário tocou em algum campo de rede IP. Útil para
// decidir se config_network deve ser enviado.
function networkTouched(d) {
  if (!d) return false;
  // dhcp=false explícito é "toque" — usuário desabilitou DHCP
  if (d.dhcp === false) return true;
  // Algum campo IP preenchido (não vazio nem default-só) também conta
  const fields = ["ip", "gateway", "dns1", "dns2"];
  return fields.some((f) => typeof d[f] === "string" && d[f].trim().length > 0);
}

// Constrói o payload de config_network. Retorna null se não deve ser enviado.
function sanitizeNetwork(d) {
  if (!d) return null;
  if (!networkTouched(d)) return null;
  if (d.dhcp !== false) {
    // dhcp=true: envia apenas { dhcp: true }, sem campos extras
    return { dhcp: true };
  }
  // dhcp=false: ip/mask/gateway/dns1 obrigatórios; dns2 opcional
  const out = { dhcp: false };
  if (d.ip)      out.ip      = d.ip.trim();
  if (d.mask)    out.mask    = d.mask.trim();
  if (d.gateway) out.gateway = d.gateway.trim();
  if (d.dns1)    out.dns1    = d.dns1.trim();
  if (d.dns2)    out.dns2    = d.dns2.trim();
  return out;
}

// Validação client-side de config_network. Retorna string com erro amigável
// ou null se OK. Permite chamada do submit.
function validateNetwork(d) {
  if (!d || !networkTouched(d)) return null;
  if (d.dhcp !== false) {
    // dhcp=true: tem campos preenchidos? Avisar para usar DHCP puro
    const stale = ["ip", "gateway", "dns1", "dns2"]
      .filter((f) => typeof d[f] === "string" && d[f].trim().length > 0);
    if (stale.length > 0) {
      return (
        "Rede: DHCP marcado mas há campos IP preenchidos (" +
        stale.join(", ") +
        "). Desmarque DHCP para usar IP fixo, ou limpe esses campos."
      );
    }
    return null;
  }
  // dhcp=false: validar todos obrigatórios + ipv4 sintático
  const missing = [];
  if (!d.ip || !d.ip.trim())           missing.push("IP");
  if (!d.mask || !d.mask.trim())       missing.push("máscara");
  if (!d.gateway || !d.gateway.trim()) missing.push("gateway");
  if (!d.dns1 || !d.dns1.trim())       missing.push("DNS primário");
  if (missing.length > 0) {
    return (
      "Para IP fixo, preencha " +
      missing.join(", ") +
      "."
    );
  }
  // Sintaxe IPv4
  const badSyntax = [];
  for (const f of ["ip", "mask", "gateway", "dns1"]) {
    if (!_ipv4Ok(d[f])) badSyntax.push(`${f}=${d[f]}`);
  }
  if (d.dns2 && d.dns2.trim() && !_ipv4Ok(d.dns2)) {
    badSyntax.push(`dns2=${d.dns2}`);
  }
  if (badSyntax.length > 0) {
    return "Rede: IP(s) com formato inválido (" + badSyntax.join(", ") + "). Use IPv4 ponto-decimal (ex: 192.168.1.10).";
  }
  return null;
}
